/**
 * Shared Plaid category constants, colors, and helpers.
 * Used across chart, table, and filter components in the banking module.
 *
 * Colors are generated deterministically from category key via djb2 hash → HSL.
 * The same key always produces the same color, regardless of list order or session.
 * No hardcoded color palette — fully dynamic and scalable.
 */

/**
 * djb2 string hash — returns a deterministic positive 32-bit integer.
 * Pure function: same input always yields same output, no external state.
 */
function hashString(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0
  }
  return Math.abs(hash)
}

/**
 * In-memory cache for generated colors — avoids recalculation within a session.
 * Capped at MAX_CACHE_SIZE entries as a safety measure.
 * In practice, Plaid has ~30 primary categories so this limit is never reached.
 */
const colorCache = new Map<string, string>()
const MAX_CACHE_SIZE = 200

/**
 * Generate a visually distinct HSL color from a category key using a hash.
 * Hue is derived from the hash for maximum spread across the color wheel.
 * Saturation and lightness are slightly varied using different hash bits
 * so that similar hues still look distinct.
 * The result is stable: the same key always produces the same color.
 */
function generateCategoryColor(key: string): string {
  const cached = colorCache.get(key)
  if (cached) return cached

  const hash = hashString(key)
  const hue = hash % 360
  const saturation = 60 + (hash % 21)       // 60–80%
  const lightness = 42 + ((hash >> 8) % 14)  // 42–55%

  const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`
  if (colorCache.size < MAX_CACHE_SIZE) {
    colorCache.set(key, color)
  }
  return color
}

/** Human-readable labels for known Plaid categories */
export const CATEGORY_LABELS: Record<string, string> = {
  INCOME: 'Income',
  FOOD_AND_DRINK: 'Food & Drink',
  TRANSPORTATION: 'Transportation',
  ENTERTAINMENT: 'Entertainment',
  GENERAL_MERCHANDISE: 'Shopping',
  RENT_AND_UTILITIES: 'Bills & Utilities',
  TRANSFER_OUT: 'Transfers Out',
  TRANSFER_IN: 'Transfers In',
  LOAN_PAYMENTS: 'Loan Payments',
  BANK_FEES: 'Bank Fees',
  MEDICAL: 'Medical',
  PERSONAL_CARE: 'Personal Care',
  TRAVEL: 'Travel',
  OTHER: 'Other',
}

/**
 * Get color for a category key.
 * All colors are generated dynamically from a djb2 hash of the key.
 * The result is deterministic — same key always produces the same HSL color.
 * @param key - Plaid category key (e.g. 'INCOME')
 */
export function getCategoryColor(key: string): string {
  return generateCategoryColor(key)
}

/**
 * Get human-readable label for a category key.
 * Falls back to title-casing the key with underscores replaced by spaces.
 * @param key - Plaid category key (e.g. 'FOOD_AND_DRINK' → 'Food & Drink')
 */
export function getCategoryLabel(key: string): string {
  if (CATEGORY_LABELS[key]) return CATEGORY_LABELS[key]
  // Fallback: title-case for valid UPPER_SNAKE keys; return as-is for unexpected format
  if (!/^[A-Z][A-Z_]*$/.test(key)) return key
  return key.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Plaid personal_finance_category.primary values for filter dropdowns.
 * These match the raw strings stored in transactions.plaid_category column.
 */
export const PLAID_CATEGORIES = [
  { value: 'INCOME', label: 'Income' },
  { value: 'FOOD_AND_DRINK', label: 'Food & Drink' },
  { value: 'TRANSPORTATION', label: 'Transportation' },
  { value: 'ENTERTAINMENT', label: 'Entertainment' },
  { value: 'GENERAL_MERCHANDISE', label: 'Shopping' },
  { value: 'RENT_AND_UTILITIES', label: 'Bills & Utilities' },
  { value: 'TRANSFER_OUT', label: 'Transfers Out' },
  { value: 'TRANSFER_IN', label: 'Transfers In' },
  { value: 'LOAN_PAYMENTS', label: 'Loan Payments' },
  { value: 'BANK_FEES', label: 'Bank Fees' },
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'PERSONAL_CARE', label: 'Personal Care' },
  { value: 'TRAVEL', label: 'Travel' },
]
